import usePatientPhotos from "@/features/clinical-actions/usePatientPhotos.js";
// src/pages/doctors/appointments/DoctorEhrRequest.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { supabase } from "@/services/supabaseClient";
import { ehrStatusFromServer, submitConfirmedEhrRequest } from "@/services/confirmedEhrRequest.mjs";

import Divider from "@/components/ui/Divider.jsx";
import Card from "@/components/ui/Card.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import ListItem56px from "@/sot/primitives/ListItem56px.jsx";
import { loadDoctorAppointmentSnapshot } from "@/pages/doctors/appointments/lib/doctorAppointmentsData.js";
import {
  loadAppointmentEhrAccessState,
  requestAppointmentEhrAccess,
  resolvePatientAuthUserId,
} from "@/services/ehrAccessService.js";
import { getAppointmentLifecycleBucket } from "@/services/appointmentLifecycle.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";
import { buildAppointmentSmsConfirmation } from "@/services/accessLayerSmsService.js";

import { formatLongDate } from "@/utils/date";

// ✅ Keep icon map identical to DoctorAppointmentDetails to avoid mismatch
const ICONS = {
  back: "/icons/doctor/appointments/arrow_back.svg",
  next: "/icons/doctor/appointments/navigate_next.svg",

  chat: "/icons/doctor/appointments/chat.svg",
  sms: "/icons/doctor/appointments/chat.svg",
  call: "/icons/doctor/appointments/call.svg",

  schedule: "/icons/doctor/appointments/schedule.svg",
  first: "/icons/doctor/appointments/first_appointment.svg",
  evisit: "/icons/appointments/afrimed-EVisit.svg",
  meet: "/icons/afrimed-video.svg",
  hospital: "/icons/doctor/appointments/hospital_Fill.svg",

  pending: "/icons/doctor/appointments/Pending.svg",
  check: "/icons/doctor/appointments/check.svg",
  verified: "/icons/doctor/appointments/verified.svg",
  pendingVerification: "/icons/pending-verification.svg",

  ehr: "/icons/doctor/appointments/EHR.svg",
  requestEhr: "/icons/doctor/appointments/Request_EHR.svg", // ✅ 64 x 64
  accessRequested: "/icons/doctor/appointments/access_requested.svg", // 24 x 24
  requestGranted: "/icons/doctor/appointments/EHR/request_granted.svg",

  change: "/icons/doctor/appointments/change_appointment.svg",
};

function safeJSONParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function restoreDraft(id, locationState) {
  if (locationState && Object.keys(locationState || {}).length) return locationState;

  try {
    const raw = sessionStorage.getItem(`afrimed:doctor_appt:${String(id)}`);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed) return parsed;
  } catch {}

  return null;
}

function persistDraft(id, payload) {
  if (!payload) return;
  try {
    sessionStorage.setItem(`afrimed:doctor_appt:${String(id)}`, JSON.stringify(payload));
  } catch {}
}

function normalizeStatus(raw) {
  const s = String(raw || "").toLowerCase();
  if (s.includes("pend")) return "pending";
  if (s.includes("confirm")) return "confirmed";
  return raw ? s : "confirmed";
}

function normalizeVisitType(restored) {
  const raw =
    restored?.visitType ??
    restored?.mode ??
    restored?.appointmentType ??
    restored?.consultation_type ??
    (restored?.isEVisit ? "evisit" : null);

  const s = String(raw || "").toLowerCase();
  if (s === "evisit" || s === "virtual" || s === "private" || s === "telehealth") return "evisit";
  return "inperson";
}

function isMeaningfulText(value) {
  const text = String(value || "").trim();
  return Boolean(text && text !== "—" && text !== "-");
}

function firstMeaningfulText(values = [], fallback = "") {
  for (const value of values) {
    if (value === 0 || value === false) return String(value);
    if (isMeaningfulText(value)) return String(value).trim();
  }
  return fallback;
}

function canShowAccessLayerSmsPreview(search = "") {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(String(search || ""));
  if (params.get("debugAccessLayer") === "1" || params.get("debugSms") === "1") return true;
  const host = String(window.location?.host || "").toLowerCase();
  return (
    host.includes("localhost") ||
    host.includes("127.0.0.1") ||
    host === "staging.afrimed.tech" ||
    host.endsWith("--afrimed-staging.netlify.app")
  );
}

function shouldOpenAccessLayerSmsPreview(search = "") {
  const params = new URLSearchParams(String(search || ""));
  return params.get("debugAccessLayer") === "1" || params.get("debugSms") === "1";
}

function canShowAccessLayerSmsEvidence(search = "") {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(String(search || ""));
  if (params.get("debugSms") !== "1" && params.get("debugAccessLayer") !== "1") return false;
  const host = String(window.location?.host || "").toLowerCase();
  return (
    host.includes("localhost") ||
    host.includes("127.0.0.1") ||
    host === "staging.afrimed.tech" ||
    host.endsWith("--afrimed-staging.netlify.app")
  );
}

function formatAccessLayerDispatchTimestamp(value = "") {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return String(value || "");
  }
}

function formatAccessLayerProvider(value = "") {
  const provider = String(value || "").trim();
  if (provider.toLowerCase() === "twilio") return "Twilio";
  return provider || "Not available";
}

function appointmentDateToHHMM(value) {
  const date = coerceDate(value);
  if (!date) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function appointmentDateToEndHHMM(value, durationMinutes = 0) {
  const date = coerceDate(value);
  const duration = Number(durationMinutes || 0);
  if (!date || !Number.isFinite(duration) || duration <= 0) return "";
  const end = new Date(date.getTime() + duration * 60 * 1000);
  return `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;
}

function appointmentDateToISO(value) {
  const date = coerceDate(value);
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function calcAge(dobISO) {
  if (!dobISO) return null;
  const d = new Date(dobISO);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age <= 120 ? age : null;
}

function formatAmount(value, currency = "") {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const code = String(currency || "").trim().toUpperCase();
  return code ? `${numeric.toFixed(2)} ${code}` : numeric.toFixed(2);
}

function coerceDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function deriveStartAt(restored) {
  const explicit = coerceDate(restored?.appointment_date || restored?.when?.startAt || restored?.start_at);
  if (explicit) return explicit;

  const datePart = String(restored?.dateISO || restored?.selectedDate || restored?.when?.dateISO || "").slice(0, 10);
  const timePart = String(restored?.start || restored?.selectedTime || restored?.when?.time || "").trim();
  if (!datePart) return null;

  const composed = new Date(`${datePart}T${timePart || "00:00"}:00`);
  return Number.isNaN(composed.getTime()) ? null : composed;
}

function resolvePatientUserId(...candidates) {
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (value) return value;
  }
  return null;
}

function mergeSnapshot(primarySnapshot, fallbackSnapshot) {
  if (!primarySnapshot) return fallbackSnapshot || null;
  if (!fallbackSnapshot) return primarySnapshot;

  const fallbackPatient = fallbackSnapshot?.patient || fallbackSnapshot?.patientProfile || {};
  const primaryPatient = primarySnapshot?.patient || primarySnapshot?.patientProfile || {};

  return {
    ...fallbackSnapshot,
    ...primarySnapshot,
    patient: {
      ...fallbackPatient,
      ...primaryPatient,
    },
    patientProfile: {
      ...fallbackPatient,
      ...fallbackSnapshot?.patientProfile,
      ...primaryPatient,
      ...primarySnapshot?.patientProfile,
    },
    doctor: {
      ...(fallbackSnapshot?.doctor || {}),
      ...(primarySnapshot?.doctor || {}),
    },
    facility: {
      ...(fallbackSnapshot?.facility || {}),
      ...(primarySnapshot?.facility || {}),
    },
    when: {
      ...(fallbackSnapshot?.when || {}),
      ...(primarySnapshot?.when || {}),
    },
    ehrSummary: {
      ...(fallbackSnapshot?.ehrSummary || {}),
      ...(primarySnapshot?.ehrSummary || {}),
    },
    patientPhotos:
      Array.isArray(primarySnapshot?.patientPhotos) && primarySnapshot.patientPhotos.length > 0
        ? primarySnapshot.patientPhotos
        : fallbackSnapshot?.patientPhotos || [],
  };
}

export default function DoctorEhrRequest() {
  const nav = useNavigate();
  const { appointmentId } = useParams();
  const location = useLocation();

  // strip one-time flags (keep state clean)
  const stateForRestore = React.useMemo(() => {
  const s = location.state || null;
  if (!s) return null;

  const { justConfirmed: _a, justRescheduled: _b, justDeleted: _c, ...rest } = s;

  // ✅ Critical: if state belongs to a DIFFERENT appointment, ignore it.
  // This prevents "apt_tlotlo" hijacking when you open a different URL.
  const stateAppt = String(rest?.appointmentId || rest?.id || "");
  const urlAppt = String(appointmentId || "");

  if (stateAppt && urlAppt && stateAppt !== urlAppt) return null;

  return rest;
}, [location.state, appointmentId]);

  const restoredDraft = React.useMemo(
    () => restoreDraft(appointmentId, stateForRestore),
    [appointmentId, stateForRestore]
  );

  const [dbSnapshot, setDbSnapshot] = React.useState(null);
  const [snapshotLoading, setSnapshotLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFromDb() {
      try {
        setSnapshotLoading(true);
        const { data: authData } = await supabase.auth.getUser();
        const doctorId = authData?.user?.id || null;
        const snapshot = await loadDoctorAppointmentSnapshot({
          appointmentId,
          doctorId,
        });
        if (!alive) return;
        setDbSnapshot(snapshot || null);
      } catch {
        if (!alive) return;
        setDbSnapshot(null);
      } finally {
        if (alive) setSnapshotLoading(false);
      }
    }

    hydrateFromDb();
    return () => {
      alive = false;
    };
  }, [appointmentId, restoredDraft]);

  const restored = React.useMemo(
    () => mergeSnapshot(dbSnapshot, restoredDraft),
    [dbSnapshot, restoredDraft]
  );

  React.useEffect(() => {
    if (restored) persistDraft(appointmentId, restored);
  }, [appointmentId, restored]);

  const [detailHydration, setDetailHydration] = React.useState({
    appointment: null,
    patientProfile: null,
  });
  const [toastMsg, setToastMsg] = React.useState("");
  const [accessLayerPreviewVersion, setAccessLayerPreviewVersion] = React.useState(0);
  const [accessLayerSmsOpen, setAccessLayerSmsOpen] = React.useState(() =>
    shouldOpenAccessLayerSmsPreview(location.search)
  );
  const [accessLayerSmsRefreshBusy, setAccessLayerSmsRefreshBusy] = React.useState(false);
  const [accessLayerSmsDispatchEvidence, setAccessLayerSmsDispatchEvidence] = React.useState(null);
  const [accessLayerSmsDispatchEvidenceStatus, setAccessLayerSmsDispatchEvidenceStatus] =
    React.useState("idle");

  React.useEffect(() => {
    if (!toastMsg) return undefined;
    const timeoutId = window.setTimeout(() => setToastMsg(""), 3500);
    return () => window.clearTimeout(timeoutId);
  }, [toastMsg]);

  React.useEffect(() => {
    if (shouldOpenAccessLayerSmsPreview(location.search)) {
      setAccessLayerSmsOpen(true);
    }
  }, [location.search]);

  function handleBack() {
    if (window.history.length > 1) nav(-1);
    else nav(`/doctors/appointments/${appointmentId}`);
  }

  const {items:resolvedPatientPhotos}=usePatientPhotos(appointmentId);

  React.useEffect(() => {
    let alive = true;

    async function hydrateDetailFallbacks() {
      const patientRef = resolvePatientUserId(
        restored?.patient_user_id,
        restored?.patient?.user_id,
        restored?.patientProfile?.user_id,
        restored?.patientId,
        restored?.patient_id,
      );

      let appointmentRow = null;
      let facilityRow = null;
      let patientLinkRow = null;
      let patientProfileRow = null;
      let ehrInfoRow = null;

      try {
        const { data } = await supabase
          .from("appointments")
          .select(
            "id, appointment_date, duration_minutes, status, consultation_type, practice_id, booked_service_name, booked_service_price, booked_service_currency"
          )
          .eq("id", String(appointmentId || "").trim())
          .maybeSingle();
        appointmentRow = data || null;
      } catch (error) {
        console.warn("Doctor EHR request appointment hydration failed:", error);
      }

      const practiceId = String(appointmentRow?.practice_id || "").trim();
      if (practiceId) {
        try {
          const { data } = await supabase
            .from("doctor_consultancies")
            .select("id, facility_name, facility_address, facility_city, facility_country")
            .eq("id", practiceId)
            .maybeSingle();
          facilityRow = data || null;
        } catch (error) {
          console.warn("Doctor EHR request facility hydration failed:", error);
        }
      }

      const patientLookupRef = String(patientRef || "").trim();
      if (patientLookupRef && /^[0-9a-f-]{36}$/i.test(patientLookupRef)) {
        try {
          const { data } = await supabase
            .from("patients")
            .select("id, user_id")
            .or(`id.eq.${patientLookupRef},user_id.eq.${patientLookupRef}`)
            .maybeSingle();
          patientLinkRow = data || null;
        } catch (error) {
          console.warn("Doctor EHR request patient link hydration failed:", error);
        }
      }

      const patientUserId = String(patientLinkRow?.user_id || patientLookupRef || "").trim();
      if (patientUserId && /^[0-9a-f-]{36}$/i.test(patientUserId)) {
        const [patientProfileResult, ehrInfoResult] = await Promise.allSettled([
          supabase.from("patient_profiles").select("*").eq("user_id", patientUserId).maybeSingle(),
          supabase
            .from("ehr_patient_info")
            .select(
              "patient_id, primary_language, height_cm, weight_kg, blood_type, current_medications, allergies, chronic_conditions, is_organ_donor"
            )
            .eq("patient_id", patientUserId)
            .maybeSingle(),
        ]);

        patientProfileRow =
          patientProfileResult.status === "fulfilled" ? patientProfileResult.value?.data || null : null;
        ehrInfoRow =
          ehrInfoResult.status === "fulfilled" ? ehrInfoResult.value?.data || null : null;
      }

      if (!alive) return;
      setDetailHydration({
        appointment: appointmentRow
          ? {
              ...appointmentRow,
              facilityName:
                facilityRow?.facility_name ||
                restored?.facilityName ||
                restored?.facility?.name ||
                null,
              facility:
                facilityRow || restored?.facility
                  ? {
                      id: facilityRow?.id || practiceId || restored?.facility?.id || null,
                      name:
                        facilityRow?.facility_name ||
                        restored?.facility?.name ||
                        restored?.facilityName ||
                        "",
                      address: facilityRow?.facility_address || restored?.facility?.address || "",
                      city: facilityRow?.facility_city || restored?.facility?.city || "",
                      country:
                        facilityRow?.facility_country || restored?.facility?.country || "",
                    }
                  : null,
            }
          : null,
        patientProfile:
          patientProfileRow || ehrInfoRow
            ? {
                ...(patientProfileRow || {}),
                ...(ehrInfoRow
                  ? {
                      primary_language:
                        ehrInfoRow.primary_language || patientProfileRow?.primary_language || null,
                      height_cm: ehrInfoRow.height_cm ?? patientProfileRow?.height_cm ?? null,
                      weight_kg: ehrInfoRow.weight_kg ?? patientProfileRow?.weight_kg ?? null,
                      blood_type: ehrInfoRow.blood_type || patientProfileRow?.blood_type || null,
                      current_medications:
                        ehrInfoRow.current_medications ||
                        patientProfileRow?.current_medications ||
                        null,
                      allergies: ehrInfoRow.allergies || patientProfileRow?.allergies || null,
                      chronic_conditions:
                        ehrInfoRow.chronic_conditions || patientProfileRow?.chronic_conditions || null,
                      medical_conditions:
                        patientProfileRow?.medical_conditions ||
                        ehrInfoRow.chronic_conditions ||
                        null,
                      organ_donor:
                        typeof patientProfileRow?.organ_donor === "boolean"
                          ? patientProfileRow.organ_donor
                          : typeof ehrInfoRow?.is_organ_donor === "boolean"
                            ? ehrInfoRow.is_organ_donor
                            : null,
                      is_organ_donor:
                        typeof ehrInfoRow?.is_organ_donor === "boolean"
                          ? ehrInfoRow.is_organ_donor
                          : typeof patientProfileRow?.organ_donor === "boolean"
                            ? patientProfileRow.organ_donor
                            : null,
                    }
                  : {}),
              }
            : null,
      });
    }

    hydrateDetailFallbacks();
    return () => {
      alive = false;
    };
  }, [appointmentId, restored]);

  const restoredWithPhotos = React.useMemo(
    () => ({
      ...(restored || {}),
      patientPhotos: resolvedPatientPhotos,
    }),
    [restored, resolvedPatientPhotos]
  );

const [serverEhrState, setServerEhrState] = React.useState(null);
React.useEffect(() => {
  let active = true;
  const refresh = async () => {
    try {
      const state = appointmentId
        ? await loadAppointmentEhrAccessState({ appointmentId })
        : null;
      if (active) setServerEhrState(state?.request || null);
    } catch (error) {
      console.warn("Unable to load appointment EHR request:", error);
      if (active) setServerEhrState(null);
    }
  };
  void refresh();
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") void refresh();
  };
  window.addEventListener("focus", refresh);
  document.addEventListener("visibilitychange", onVisibilityChange);
  return () => {
    active = false;
    window.removeEventListener("focus", refresh);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}, [appointmentId]);

// ---------- hydrated snapshot ----------
  const patient = restored?.patient || restored?.patientProfile || null;
  const patientUserId = resolvePatientUserId(
    restored?.patient_user_id,
    patient?.user_id,
    restored?.patient?.user_id,
    restored?.patientProfile?.user_id,
    restored?.patientId,
    restored?.patient_id,
  );
  const patientRowId = resolvePatientUserId(
    restored?.patient_row_id,
    restored?.patient_ref,
    patient?.patient_id,
    restored?.patient?.patient_id,
    restored?.patientProfile?.patient_id,
    restored?.patient_id,
  );

const ehrStatusStable = ehrStatusFromServer(serverEhrState);
const ehrIsRequestedStable = ehrStatusStable === "requested";

  const patientProfilePayload = React.useMemo(
    () => ({
      ...(restored?.patientProfile || {}),
      ...(patient || {}),
      ...(detailHydration.patientProfile || {}),
    }),
    [detailHydration.patientProfile, patient, restored?.patientProfile]
  );

  const patientName =
    patient?.full_name ||
    patient?.name ||
    patientProfilePayload?.full_name ||
    patientProfilePayload?.name ||
    restored?.patient_name ||
    restored?.patientName ||
    "Patient";

  const avatar =
    patient?.avatar_url ||
    patient?.avatar ||
    restored?.avatar ||
    restored?.patient_avatar ||
    "/icons/circular-avatar-placeholder.svg";

  const sex =
    patientProfilePayload?.sex ??
    patientProfilePayload?.gender ??
    restored?.sex ??
    restored?.gender ??
    null;

  const dob =
    patientProfilePayload?.dob ??
    patientProfilePayload?.date_of_birth ??
    patientProfilePayload?.dateOfBirth ??
    restored?.dob ??
    restored?.date_of_birth ??
    null;

  const computedAge = calcAge(dob);

  const numericAgeRaw =
    patient?.age ??
    patient?.patient_age ??
    restored?.age ??
    restored?.patient_age ??
    null;

  const numericAge =
    typeof numericAgeRaw === "number"
      ? numericAgeRaw
      : typeof numericAgeRaw === "string"
        ? Number.parseInt(numericAgeRaw, 10)
        : null;

  const age = computedAge ?? (Number.isFinite(numericAge) ? numericAge : null);

  const appointmentDateTime = firstMeaningfulText(
    [detailHydration.appointment?.appointment_date, restored?.appointment_date],
    ""
  );
  const appointmentDurationMinutes = Number(
    detailHydration.appointment?.duration_minutes ??
      restored?.duration_minutes ??
      restored?.durationMinutes ??
      restored?.when?.duration_mins ??
      0
  );
  const status = normalizeStatus(detailHydration.appointment?.status || restored?.status);
  const visitType = normalizeVisitType({
    ...(restored || {}),
    consultation_type:
      detailHydration.appointment?.consultation_type || restored?.consultation_type || null,
  });

  const facilityName = firstMeaningfulText(
    [
      detailHydration.appointment?.facilityName,
      detailHydration.appointment?.facility?.name,
      restored?.facilityName,
      restored?.facility?.name,
      restored?.facility_name,
    ],
    "Consultation location"
  );

  const facilityAddress = firstMeaningfulText(
    [
      [
        detailHydration.appointment?.facility?.address,
        detailHydration.appointment?.facility?.city,
        detailHydration.appointment?.facility?.country,
      ]
        .filter(Boolean)
        .join(", "),
      [
        restored?.facility?.address,
        restored?.facility?.city,
        restored?.facility?.country,
      ]
        .filter(Boolean)
        .join(", "),
      restored?.facility_address,
      restored?.displayLocation,
    ],
    ""
  );

  const startTime = firstMeaningfulText(
    [
      appointmentDateToHHMM(appointmentDateTime),
      restored?.start,
      restored?.selectedTime,
      restored?.when?.time,
    ],
    ""
  );

  const endTime = firstMeaningfulText(
    [
      appointmentDateToEndHHMM(appointmentDateTime, appointmentDurationMinutes),
      restored?.end,
      restored?.endTime,
      restored?.when?.end,
    ],
    ""
  );

  const dateISO = firstMeaningfulText(
    [
      appointmentDateToISO(appointmentDateTime),
      restored?.dateISO,
      restored?.selectedDate,
      restored?.when?.dateISO,
      restored?.when?.date,
    ],
    ""
  );

  const dateLabel = dateISO ? formatLongDate(dateISO) : "—";

  const apptTypeLabel = firstMeaningfulText(
    [
      detailHydration.appointment?.booked_service_name,
      String(detailHydration.appointment?.consultation_type || "").toLowerCase() === "private"
        ? "Virtual"
        : "",
      restored?.serviceName,
      restored?.service_name,
      restored?.booked_service_name,
      restored?.serviceLabel,
      restored?.subtitle,
      restored?.appointment_type,
      restored?.typeLabel,
    ],
    "Consultation"
  );

  const appointmentPriceLabel = firstMeaningfulText(
    [
      restored?.serviceNamePrice,
      restored?.serviceLabelPrice,
      formatAmount(
        detailHydration.appointment?.booked_service_price ??
          restored?.amountPaid ??
          restored?.booked_service_price,
        detailHydration.appointment?.booked_service_currency ||
          restored?.currency ||
          restored?.booked_service_currency ||
          "ZAR"
      ),
    ],
    "—"
  );

  const meetUrl =
    restored?.meetUrl ||
    restored?.meet_url ||
    "";

  const notes =
    restored?.notes ||
    restored?.patient_notes ||
    "";

  const [notesDraft, setNotesDraft] = React.useState(notes);

  React.useEffect(() => {
    setNotesDraft(notes);
  }, [appointmentId, notes]);

  const handleNotesBlur = React.useCallback(async () => {
    const next = String(notesDraft || "").trim();

    try {
      const key = `afrimed:doctor_appt:${String(appointmentId)}`;
      const raw = sessionStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : {};
      sessionStorage.setItem(key, JSON.stringify({ ...parsed, notes: next }));
    } catch {}

    try {
      const { error } = await supabase
        .from("appointments")
        .update({ notes: next, updated_at: new Date().toISOString() })
        .eq("id", appointmentId);

      if (error) {
        console.warn("Doctor EHR request notes autosave failed:", error);
      }
    } catch (error) {
      console.warn("Doctor EHR request notes autosave failed:", error);
    }
  }, [appointmentId, notesDraft]);

  const lifecycleBucket = getAppointmentLifecycleBucket(
    {
      appointment_date: appointmentDateTime || restored?.appointment_date || null,
      selectedDate: dateISO || null,
      selectedTime: startTime || null,
      consultation_type: visitType === "evisit" ? "private" : "moh",
      status,
    },
    Date.now()
  );
  const isPastBooking = lifecycleBucket === "past";
  const canOpenLiveCall = visitType === "evisit" && status === "in_progress";
  const patientVerificationStatus = normalizeVerificationStatus(
    patientProfilePayload?.id_verification_status ||
      patientProfilePayload?.verification_status ||
      patient?.id_verification_status ||
      patient?.verification_status ||
      restored?.patient?.id_verification_status ||
      restored?.patient?.verification_status ||
      restored?.patientProfile?.id_verification_status ||
      restored?.patientProfile?.verification_status,
    "pending"
  );
  const patientVerificationIcon =
    patientVerificationStatus === "verified"
      ? ICONS.verified
      : ICONS.pendingVerification;

  const patientMeta = [
    age != null ? `${age} Years old` : null,
    sex ? String(sex).charAt(0).toUpperCase() + String(sex).slice(1) : null,
  ]
    .filter(Boolean)
    .join(", ");

  const accessLayerSmsPreviewVisible = canShowAccessLayerSmsPreview(location.search);
  const accessLayerSmsEvidenceVisible = canShowAccessLayerSmsEvidence(location.search);
  const accessLayerRecipientPhone = firstMeaningfulText(
    [
      patientProfilePayload?.phone,
      patient?.phone,
      restored?.patientProfile?.phone,
      restored?.patient?.phone,
      restored?.patient_phone,
      restored?.patientPhone,
    ],
    ""
  );
  const accessLayerSmsPreview = React.useMemo(
    () =>
      buildAppointmentSmsConfirmation({
        appointment: {
          id: appointmentId,
          appointment_date: appointmentDateTime,
          patientPhone: accessLayerRecipientPhone,
        },
      }),
    [accessLayerPreviewVersion, accessLayerRecipientPhone, appointmentDateTime, appointmentId]
  );

  React.useEffect(() => {
    if (!accessLayerSmsEvidenceVisible || !appointmentId) {
      setAccessLayerSmsDispatchEvidence(null);
      setAccessLayerSmsDispatchEvidenceStatus("idle");
      return undefined;
    }

    let alive = true;

    async function loadAccessLayerDispatchEvidence() {
      setAccessLayerSmsDispatchEvidenceStatus("loading");
      const { data, error } = await supabase
        .from("communication_dispatches")
        .select(
          [
            "id",
            "channel",
            "purpose",
            "recipient_phone",
            "message_body",
            "reference_code",
            "provider",
            "provider_message_id",
            "status",
            "is_test",
            "metadata_json",
            "created_at",
            "sent_at",
          ].join(",")
        )
        .eq("appointment_id", appointmentId)
        .eq("channel", "sms")
        .eq("purpose", "appointment_confirmation_test_sms")
        .eq("is_test", true)
        .order("created_at", { ascending: false })
        .limit(1);

      if (!alive) return;
      if (error) {
        setAccessLayerSmsDispatchEvidence(null);
        setAccessLayerSmsDispatchEvidenceStatus("error");
        return;
      }

      setAccessLayerSmsDispatchEvidence(Array.isArray(data) ? data[0] || null : null);
      setAccessLayerSmsDispatchEvidenceStatus("loaded");
    }

    loadAccessLayerDispatchEvidence();

    return () => {
      alive = false;
    };
  }, [accessLayerPreviewVersion, accessLayerSmsEvidenceVisible, appointmentId]);

  const accessLayerSmsDispatchMetadata =
    accessLayerSmsDispatchEvidence?.metadata_json &&
    typeof accessLayerSmsDispatchEvidence.metadata_json === "object"
      ? accessLayerSmsDispatchEvidence.metadata_json
      : {};
  const accessLayerSmsDispatchSentAt = formatAccessLayerDispatchTimestamp(
    accessLayerSmsDispatchEvidence?.sent_at || accessLayerSmsDispatchEvidence?.created_at
  );
  const accessLayerSmsDispatchSensitiveDataExcluded =
    accessLayerSmsDispatchMetadata.sensitive_data_excluded !== false;

  function refreshAccessLayerSmsPreview() {
    if (accessLayerSmsRefreshBusy) return;
    setAccessLayerSmsRefreshBusy(true);
    setAccessLayerPreviewVersion((current) => current + 1);
    window.setTimeout(() => {
      setAccessLayerSmsRefreshBusy(false);
    }, 450);
  }

  function handleMessageClick() {
    if (isPastBooking) return;
    nav(`/messages/${patientUserId || appointmentId}`, {
      state: {
        conversationTitle: patientName,
        conversationType: "patient",
        appointmentId: String(appointmentId),
        patientId: patientUserId || null,
        returnTo: location.pathname,
      },
    });
  }

  function handleSmsPreviewClick() {
    if (isPastBooking) return;
    setAccessLayerSmsOpen((current) => !current);
  }

  function handleCallClick() {
    if (isPastBooking) return;
    if (!canOpenLiveCall) {
      setToastMsg("Only available for appointments");
      return;
    }

    nav(`/appointments/call/${appointmentId}`, {
      state: {
        appointmentId: String(appointmentId),
        patientId: patientUserId || null,
        patient: patientProfilePayload,
        restored: restored || null,
      },
    });
  }

  const accessLayerSmsPreviewCard = accessLayerSmsPreviewVisible && accessLayerSmsOpen ? (
    <Card className="mt-4 overflow-hidden">
      <div className="px-4 py-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="text-title-medium text-on-surface">Access Layer SMS preview</div>
            <div className="mt-1 text-body-medium text-on-surface-var">
              Preview only — SMS provider not enabled
            </div>
          </div>
          <button
            type="button"
            disabled={accessLayerSmsRefreshBusy}
            onClick={refreshAccessLayerSmsPreview}
            className="inline-flex h-10 items-center gap-2 rounded-[12px] bg-[var(--state-primary-12)] px-4 text-label-medium text-[rgb(var(--afrimed-primary))] shadow-[var(--elev-1)] disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${accessLayerSmsRefreshBusy ? "animate-spin" : ""}`} aria-hidden />
            {accessLayerSmsRefreshBusy ? "Refreshing..." : "Refresh preview"}
          </button>
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3 grid gap-3 md:grid-cols-2">
        <div>
          <div className="text-body-small text-on-surface-var">Appointment reference</div>
          <div className="mt-1 text-body-large text-on-surface">
            {accessLayerSmsPreview?.referenceCode || "AFM-PREVIEW"}
          </div>
        </div>
        <div>
          <div className="text-body-small text-on-surface-var">Recipient phone</div>
          <div className="mt-1 text-body-large text-on-surface">
            {accessLayerSmsPreview?.recipientPhone || "Not available"}
          </div>
        </div>
        <div>
          <div className="text-body-small text-on-surface-var">Appointment date/time</div>
          <div className="mt-1 text-body-large text-on-surface">
            {dateLabel !== "—" && startTime ? `${dateLabel}, ${startTime}` : "Scheduled time"}
          </div>
        </div>
        <div>
          <div className="text-body-small text-on-surface-var">Provider state</div>
          <div className="mt-1 text-body-large text-on-surface">
            {accessLayerSmsEvidenceVisible ? "twilio_test_proof_available" : "provider_not_configured"}
          </div>
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3">
        <div className="text-body-small text-on-surface-var">Message preview</div>
        <div className="mt-2 rounded-[12px] bg-[rgba(var(--clr-on-surface),0.04)] px-3 py-3 text-body-medium leading-6 text-on-surface">
          {accessLayerSmsPreview?.message}
        </div>
        <div className="mt-3 text-body-small text-on-surface-var">
          Sensitive data excluded: <span className="text-on-surface">Yes</span>
        </div>
      </div>

      {accessLayerSmsEvidenceVisible ? (
        <>
          <Divider inset variant="outlineVariant" />
          <div className="px-4 py-3">
            <div className="text-title-small text-on-surface">Controlled SMS dispatch evidence</div>
            {accessLayerSmsDispatchEvidenceStatus === "loading" ? (
              <div className="mt-3 text-body-small text-on-surface-var">Loading dispatch evidence...</div>
            ) : null}
            {accessLayerSmsDispatchEvidence ? (
              <>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="text-body-small text-on-surface-var">Dispatch status</div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.status || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">Provider</div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {formatAccessLayerProvider(accessLayerSmsDispatchEvidence.provider)}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">Twilio Message SID</div>
                    <div className="mt-1 break-all text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.provider_message_id || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">Dispatch ID</div>
                    <div className="mt-1 break-all text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.id || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">Recipient</div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.recipient_phone || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">Sent at</div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchSentAt || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">Appointment reference</div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.reference_code || "Not available"}
                    </div>
                  </div>
                  <div>
                    <div className="text-body-small text-on-surface-var">Test flag</div>
                    <div className="mt-1 text-body-large text-on-surface">
                      {accessLayerSmsDispatchEvidence.is_test ? "Yes" : "No"}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-body-small text-on-surface-var">
                  Sensitive data excluded:{" "}
                  <span className="text-on-surface">
                    {accessLayerSmsDispatchSensitiveDataExcluded ? "Yes" : "No"}
                  </span>
                </div>
              </>
            ) : accessLayerSmsDispatchEvidenceStatus === "loaded" ? (
              <div className="mt-3 text-body-small text-on-surface-var">
                No controlled SMS dispatch evidence is stored for this appointment yet.
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </Card>
  ) : null;

  if (!restored && snapshotLoading) {
    return (
      <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
          <TopBarCenter
            title="Appointment details"
            onBack={handleBack}
            nakedBack
            backIconSrc={ICONS.back}
          />

          <Card className="mt-6 px-4 py-4 text-body-medium text-on-surface-var">
            Loading appointment details...
          </Card>
        </div>
      </div>
    );
  }

  if (!restored) {
    return (
      <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
          <TopBarCenter
            title="Appointment details"
            onBack={handleBack}
            nakedBack
            backIconSrc={ICONS.back}
            />

          <Card className="mt-6 p-4 text-body-medium text-on-surface-var">
            Appointment not found. Please go back and open it again.
          </Card>
        </div>
      </div>
    );
  }

  const isPending = status === "pending";

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
            title="Appointment details"
            onBack={handleBack}
            nakedBack
            backIconSrc={ICONS.back}
            />

        {toastMsg ? (
          <div
            className="
              fixed left-1/2 -translate-x-1/2
              bottom-[92px] md:bottom-6
              w-full max-w-[720px] px-4
              z-[80]
            "
          >
            <div
              className="
                w-full
                rounded-[12px]
                bg-[#0B0F1A]
                text-white
                px-4 py-3
                flex items-center justify-between gap-3
                shadow-[0_10px_30px_rgba(2,6,23,0.35)]
              "
              role="status"
              aria-live="polite"
            >
              <div className="text-body-medium truncate">{toastMsg}</div>
              <button
                type="button"
                onClick={() => setToastMsg("")}
                aria-label="Close"
                className="
                  shrink-0
                  h-9 w-9
                  rounded-[10px]
                  bg-[rgba(255,255,255,0.08)]
                  hover:bg-[rgba(255,255,255,0.12)]
                  active:bg-[rgba(255,255,255,0.12)]
                  flex items-center justify-center
                "
              >
                <span className="text-white text-[18px] leading-none translate-y-[-1px]">×</span>
              </button>
            </div>
          </div>
        ) : null}

        {/* Patient row + Profile (same as details) */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={avatar}
              alt={patientName}
              className="h-11 w-11 rounded-full object-cover shrink-0"
              draggable="false"
            />

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-body-large text-on-surface truncate">{patientName}</div>
                <img src={patientVerificationIcon} alt="" className="h-4 w-4 shrink-0" draggable="false" />
              </div>
              <div className="text-body-medium text-on-surface-var truncate">
                {patientMeta || "—"}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() =>
            nav("/patient/profile", {
                state: {
                // hydrate immediately (no DB yet)
                from: "doctor",
                patientId: patientRowId || null,
                patientUserId: patientUserId || null,
                patient: patient || null,
                appointmentId,
                restored: restored || null,
                },
            })
            }
            className="flex items-center gap-1 text-body-medium text-on-surface-var"
          >
            <span>Profile</span>
            <img src={ICONS.next} alt="" className="h-6 w-6 opacity-70" draggable="false" />
          </button>
        </div>

        {/* Message / SMS / Call buttons */}
        <div className={`mt-4 grid gap-4 ${accessLayerSmsPreviewVisible ? "grid-cols-3" : "grid-cols-2"}`}>
          <button
            type="button"
            disabled={isPastBooking}
            className="
              flex-1 h-12 rounded-[12px]
              bg-[rgba(86,106,139,0.08)]
              text-[rgb(var(--afrimed-primary))]
              text-label-large
              flex items-center justify-center gap-2
              disabled:cursor-not-allowed disabled:opacity-100
              disabled:bg-[rgba(86,106,139,0.08)]
              disabled:text-[rgb(var(--afrimed-primary))]
              disabled:[&_img]:opacity-100
            "
            onClick={handleMessageClick}
          >
            <img src={ICONS.chat} alt="" className="h-[18px] w-[18px]" draggable="false" />
            Message
          </button>

          {accessLayerSmsPreviewVisible ? (
            <button
              type="button"
              disabled={isPastBooking}
              className="
                flex-1 h-12 rounded-[12px]
                bg-[rgba(86,106,139,0.08)]
                text-[rgb(var(--afrimed-primary))]
                text-label-large
                flex items-center justify-center gap-2
                disabled:cursor-not-allowed disabled:opacity-100
                disabled:bg-[rgba(86,106,139,0.08)]
                disabled:text-[rgb(var(--afrimed-primary))]
                disabled:[&_img]:opacity-100
              "
              onClick={handleSmsPreviewClick}
              aria-expanded={accessLayerSmsOpen}
            >
              <img src={ICONS.sms} alt="" className="h-[18px] w-[18px]" draggable="false" />
              SMS
            </button>
          ) : null}

          <button
            type="button"
            disabled={isPastBooking}
            className="
              flex-1 h-12 rounded-[12px]
              bg-[rgba(86,106,139,0.08)]
              text-[rgb(var(--afrimed-primary))]
              text-label-large
              flex items-center justify-center gap-2
              disabled:cursor-not-allowed disabled:opacity-100
              disabled:bg-[rgba(86,106,139,0.08)]
              disabled:text-[rgb(var(--afrimed-primary))]
              disabled:[&_img]:opacity-100
            "
            onClick={handleCallClick}
          >
            <img src={ICONS.call} alt="" className="h-6 w-6" draggable="false" />
            Call
          </button>
        </div>

        {accessLayerSmsPreviewCard}

        {/* Appointment summary card (same as details) */}
        <Card className="mt-4 overflow-hidden">
          {/* time + status */}
          <div className="px-4 py-3 flex items-center gap-3">
            <img src={ICONS.schedule} alt="" className="h-6 w-6 opacity-80" draggable="false" />

            <div className="min-w-0 flex-1">
              <div className="text-body-large text-on-surface">
                {(startTime || "—")} - {(endTime || "—")}
              </div>
              <div className="text-body-medium text-on-surface-var truncate">{dateLabel}</div>
            </div>

            <div className="flex items-center gap-2 text-body-medium text-on-surface-var">
              <span className="capitalize">{isPending ? "pending" : "confirmed"}</span>
              {!isPending ? (
                <img src={ICONS.check} alt="" className="h-5 w-5" draggable="false" />
              ) : (
                <img src={ICONS.pending} alt="" className="h-5 w-5" draggable="false" />
              )}
            </div>
          </div>

          <Divider inset variant="outlineVariant" />

          {/* appointment type */}
          <div className="px-4 py-3 flex items-center gap-3">
            <img src={ICONS.first} alt="" className="h-6 w-6 opacity-80" draggable="false" />
            <div className="min-w-0 flex-1">
              <div className="text-body-large text-on-surface truncate">{apptTypeLabel}</div>
            </div>
            <div className="text-body-large text-on-surface-var">
              {appointmentPriceLabel}
            </div>
          </div>

          <Divider inset variant="outlineVariant" />

          {/* location / evisit */}
          {visitType === "evisit" ? (
            <div className="px-4 py-3 flex items-center gap-3">
              <img
                src={ICONS.evisit}
                alt=""
                className="h-6 w-6 shrink-0 opacity-80"
                draggable="false"
              />
              <div className="min-w-0 flex-1">
                <div className="text-body-large text-on-surface">Virtual</div>
                <div className="text-body-medium text-on-surface-var truncate">{meetUrl}</div>
              </div>

              <button
                type="button"
                onClick={() => console.log("Join Virtual", meetUrl)}
                disabled={!meetUrl}
                className="
                  h-10 px-4 rounded-[12px]
                  bg-[rgb(var(--afrimed-primary))]
                  text-[rgb(var(--afrimed-on-primary))]
                  text-label-large
                  flex items-center gap-2
                  disabled:opacity-50
                "
              >
                Join
              </button>
            </div>
          ) : (
            <div className="px-4 py-3 flex items-center gap-3">
              <img src={ICONS.hospital} alt="" className="h-6 w-6 opacity-80" draggable="false" />
              <div className="min-w-0 flex-1">
                <div className="text-body-large text-on-surface truncate">{facilityName}</div>
                {facilityAddress ? (
                  <div className="text-body-medium text-on-surface-var truncate">{facilityAddress}</div>
                ) : null}
              </div>
            </div>
          )}
        </Card>

      {/* Request EHR (blueprint card) */}
        <div className="mt-7">
          {/* Header label — only shown when requested (blueprint) */}
          <div className="text-title-medium text-on-surface-var mb-3">
            {ehrIsRequestedStable ? "EHR" : null}
          </div>

          {/* ---------------------------
              REQUESTED / GRANTED — blueprint status row
              Left: EHR icon + label
              Right: status text + icon
              Row click: just re-open this screen (no DB writes!)
            --------------------------- */}
          {ehrIsRequestedStable || ehrStatusStable === "granted" ? (
            <Card className="overflow-hidden">
              <ListItem56px
                variant="bare"
                divider={false}
                onClick={() => {
                  const apptId = String(appointmentId || "");
                  if (!apptId) return;

                  // Keep navigation context separate from server authority.
                  nav(`/doctors/appointments/${apptId}/ehr-request`, {
                    state: restored || null,
                  });
                }}
                leading={
                  <img
                    src={ICONS.ehr}
                    alt=""
                    className="h-6 w-6 opacity-80"
                    draggable="false"
                  />
                }
                title="EHR"
                trailing={
                  <div className="flex items-center gap-2 text-body-large text-on-surface-var">
                    <span>{ehrStatusStable === "granted" ? "Access granted" : "Access requested"}</span>
                    <img
                      src={ehrStatusStable === "granted" ? ICONS.requestGranted : ICONS.accessRequested}
                      alt=""
                      className="h-6 w-6 opacity-80"
                      draggable="false"
                    />
                  </div>
                }
              />
            </Card>
          ) : (
            /* ---------------------------
              NOT REQUESTED YET (default)
              Full request card (this is the ONLY DB writer)
              --------------------------- */
            <Card
              className="
                px-4 py-4
                min-h-[116px]
                flex items-start justify-between gap-4
              "
            >
              {/* Left */}
              <div className="min-w-0">
                <div className="text-title-medium text-on-surface">Request EHR</div>
                <div className="mt-1 text-body-medium text-on-surface-var max-w-[520px]">
                  Provide better care by accessing your patient Electronic Health Record
                </div>

                <button
                  type="button"
                  onClick={async () => {
                  try {
                    const apptId = String(appointmentId || "");
                    if (!apptId) throw new Error("Missing appointmentId");

                    // Best-effort patient id (auth user id for EHR tables)
                    const pId = resolvePatientUserId(
                      restored?.patient_user_id,
                      restored?.patient?.user_id,
                      restored?.patientProfile?.user_id,
                      patient?.user_id,
                      restored?.patientId,
                      restored?.patient_id,
                      restored?.patient?.id,
                      restored?.patientProfile?.id,
                      patient?.id,
                    );

                    // Try auth (prod/staging truth)
                    const { data: authData, error: authErr } = await supabase.auth.getUser();
                    const doctorId = authData?.user?.id || null;
                    const canonicalPatientId = await resolvePatientAuthUserId({
                      appointmentId: apptId,
                      patientUserId: pId,
                      patientRef:
                        restored?.patient_row_id ||
                        restored?.patient?.patient_id ||
                        restored?.patientProfile?.patient_id ||
                        restored?.patient_id ||
                        null,
                    });
                    const requestResult = await submitConfirmedEhrRequest({
                      appointmentId: apptId,
                      doctorId: authErr ? null : doctorId,
                      patientId: canonicalPatientId,
                      submit: () => requestAppointmentEhrAccess({
                            appointmentId: apptId,
                            doctorId,
                            patientUserId: canonicalPatientId,
                            patientRef:
                              restored?.patient_row_id ||
                              restored?.patient?.patient_id ||
                              restored?.patientProfile?.patient_id ||
                              restored?.patient_id ||
                              null,
                            doctorName:
                              restored?.doctor?.name ||
                              restored?.doctor?.full_name ||
                              "Doctor",
                          }),
                    });

                    const nextState = {
                      ...(restored || {}),
                      patientId:
                        restored?.patient_row_id ||
                        restored?.patient?.patient_id ||
                        restored?.patientProfile?.patient_id ||
                        restored?.patient_id ||
                        null,
                      patient_user_id: canonicalPatientId,
                      ehrAccessStatus: "requested",
                      ehrRequestId: requestResult.requestId,
                      ehrRequestedAt: requestResult?.requestedAt || new Date().toISOString(),
                    };

                    // persist BEFORE navigating (prevents refresh losing state)
                    try {
                      sessionStorage.setItem(
                        `afrimed:doctor_appt:${String(apptId)}`,
                        JSON.stringify(nextState)
                      );
                    } catch {}

                    setServerEhrState({ id: requestResult.requestId, status: "requested" });

                    nav(`/doctors/appointments/${apptId}/ehr-request`, {
                      state: nextState,
                      replace: true,
                    });
                  } catch (e) {
                    console.warn("EHR request failed:", e);
                    alert(e?.message || "Could not request EHR access right now. Please try again.");
                  }
                }}
                  className="mt-4 text-label-large text-[rgb(var(--afrimed-primary))]"
                >
                  Request
                </button>
              </div>

              {/* Right (big icon) */}
              <img
                src={ICONS.requestEhr}
                alt=""
                className="h-16 w-16 shrink-0 self-center"
                draggable="false"
              />
            </Card>
          )}

          {/* ---------------------------
              GRANTED — preview rows belong to the EHR section (separate block)
              We DO NOT render them here, to avoid duplicating your EHR list.
              Your existing EHR section will handle:
              - Details > (route to full EHR screen)
              - blood/allergies/chronic/donor preview rows
            --------------------------- */}
        </div>

        {/* Notes */}
        <div className="mt-7">
          <div className="text-title-medium text-on-surface-var mb-3">Notes</div>
          <Card className="px-4 py-4">
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={handleNotesBlur}
              placeholder="Add notes"
              rows={4}
              className="
                w-full resize-none outline-none
                bg-transparent
                text-body-large text-on-surface
                placeholder:text-on-surface-var
              "
            />
          </Card>
        </div>

        {/* Patient Photos */}
        <div className="mt-7">
          <div className="flex items-center justify-between">
            <div className="text-title-medium text-on-surface-var">Patient Photos</div>
            <button
              type="button"
              onClick={() => nav(`/doctors/appointments/${appointmentId}/patientphotos`, { state: restoredWithPhotos })}
              className="text-body-medium text-[rgb(var(--afrimed-primary))]"
            >
              Show all
            </button>
          </div>

          {resolvedPatientPhotos.length > 0 ? (
            <div className="mt-3 flex gap-4 overflow-x-auto no-scrollbar pr-4">
              {resolvedPatientPhotos.slice(0, 4).map((img) => (
                <div
                  key={String(img.id || img.url)}
                  className="w-[380px] h-[205px] rounded-[28px] overflow-hidden bg-[rgba(2,6,23,0.04)] shrink-0"
                >
                  <img
                    src={img.url}
                    alt="Patient photo"
                    className="w-full h-full object-cover"
                    draggable="false"
                  />
                </div>
              ))}
            </div>
          ) : (
            <Card className="mt-3 px-4 py-4 text-body-medium text-on-surface-var">
              No patient photos shared yet.
            </Card>
          )}
        </div>

        {/* Change appointment list item (blueprint order) */}
        {/* Support */}
        <div className="mt-6">
        <div className="text-title-medium text-on-surface-var mb-3">Support</div>

        <Card className="overflow-hidden">
            <button
            type="button"
            onClick={() => {
              if (isPastBooking) return;
              nav(`/appointments/details/${appointmentId}/edit`, { state: restored || null });
            }}
            disabled={isPastBooking}
            className="w-full px-4 py-4 flex items-center gap-3 text-left"
            >
            <img
              src={ICONS.change}
              alt=""
              className={`h-6 w-6 ${isPastBooking ? "opacity-40" : "opacity-80"}`}
              draggable="false"
            />
            <div className="flex-1">
              <div className={`text-body-large ${isPastBooking ? "text-on-surface-var" : "text-on-surface"}`}>
                Change appointment
              </div>
              {isPastBooking ? (
                <div className="text-body-medium text-on-surface-var">
                  Unavailable for past appointments
                </div>
              ) : null}
            </div>
            <img
              src={ICONS.next}
              alt=""
              className={`h-6 w-6 ${isPastBooking ? "opacity-30" : "opacity-70"}`}
              draggable="false"
            />
            </button>
        </Card>
        </div>

        {/* Notes / Photos / Support stay identical to Details page later.
            For v.01, we keep them OUT here unless you explicitly want them on Request screen too. */}
      </div>
    </div>
  );
}
